/*
 *  -----------------------------------------------------------------------
 *    <copyright file="secondary-panels.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */
import React, { useEffect, useState, useContext, useCallback } from 'react';
import { LocalStateContext } from 'contexts/local-state-context';
import { useBoolean, useWindowSize } from 'hooks';
import {
    PanelFields,
    DashboardFields_Dashboard_panels_DashboardParentPanel_childPanels_DashboardChildPanel as DashboardFields_panels_childPanels,
} from 'queries/generated/fragments.generated';
import CustomPager from 'views/components/core/custom-pager';
import PanelFrame, { PanelFrameContext } from 'views/components/core/panel-frame';

interface ISecondaryPanels {
    currentPanel: PanelFields & { childPanels: DashboardFields_panels_childPanels[] };
    panels: any[];
    siteId?: string;
    sitesStatusesToExclude: any;
    onOpenAddPanelDialog?: (panel: PanelFields) => void;
}

const SecondaryPanels = ({
    currentPanel,
    panels,
    siteId,
    sitesStatusesToExclude,
    onOpenAddPanelDialog,
}: ISecondaryPanels) => {
    const size = useWindowSize();
    const { state } = useContext(LocalStateContext);

    const isCollapsed = useBoolean(false);
    const bufferIsCollapsed = useBoolean(false);
    const disabled = useBoolean(false);
    const isExpandedChart = useBoolean(false);
    const [itemsForPage, setItemsForPage] = useState(size.width > 1200 ? 4 : 2);
    const [itemsForCharts, setItemsForCharts] = useState([]);
    const [containerWidth, setContainerWidth] = useState(0);

    const panelContainerRef = useCallback(
        (node: HTMLElement | null) => {
            if (node !== null) setContainerWidth(node.clientWidth);
        },
        [size],
    );

    useEffect(() => {
        if (currentPanel?.childPanels && panels?.length) {
            // Show the current (primary) panel and then all of the child panels for this dashboard
            const childPanelsArray = currentPanel?.childPanels.map((item) => item);
            const array = [currentPanel].concat(childPanelsArray?.sort((a, b) => a.index - b.index));
            let chartsArray =
                array?.map((panel) => {
                    const fullPanels = panels?.find((fullPanel: any) => fullPanel.id === panel.panelId);
                    const panelData = state.scoringData?.panelData.find(
                        (fullPanel: any) => fullPanel.panelId === panel.panelId,
                    );
                    if (panel && fullPanels && panelData) {
                        panelData.points = panelData.points?.length
                            ? panelData.points.filter((item: any) => {
                                  if (item.siteId) {
                                      const site = state.studyData.sites.find(
                                          (element: any) => item.siteId === element.Fields['Site.id'],
                                      );
                                      if (site) return sitesStatusesToExclude.indexOf(site.Fields['Site.status']) < 0;
                                  } else return true;
                              })
                            : panelData.points;
                        return { ...panel, ...fullPanels, panelData: { panelData: [{ ...panelData }] } };
                    }
                    return null;
                }) || [];
            chartsArray = chartsArray.filter(Boolean);
            setItemsForCharts(chartsArray);
        }
    }, [currentPanel, panels, sitesStatusesToExclude]);

    useEffect(() => {
        let amountPerPage = size.width >= 1070 ? Math.floor(containerWidth / 500) * 2 : 2;
        disabled.setValue(itemsForCharts?.length <= amountPerPage / 2);
        if (isCollapsed.value) amountPerPage /= 2;
        setItemsForPage(amountPerPage);
    }, [size, isCollapsed.value, containerWidth, itemsForCharts]);

    const onExpand = useCallback((value: boolean) => {
        isExpandedChart.setValue(value);
        if (value && isCollapsed.value) {
            isCollapsed.setFalse();
            bufferIsCollapsed.setTrue();
        } else {
            isCollapsed.setValue(bufferIsCollapsed.value);
            bufferIsCollapsed.setFalse();
        }
    }, []);

    return (
        <div ref={panelContainerRef}>
            <CustomPager
                itemsForPage={itemsForPage}
                isCollapseButton
                disabled={!itemsForCharts.length || disabled.value || isExpandedChart.value}
                items={itemsForCharts}
                isExpandedChart={isExpandedChart.value}
                renderChild={(panel: any) => {
                    if (panel)
                        return (
                            <div id={panel.id} className="panel-item" key={panel.id}>
                                {panel.empty ? null : (
                                    <PanelFrameContext.Provider value={{ height: 300 }}>
                                        <PanelFrame
                                            title={panel.graph.plotTitle || panel.name}
                                            panel={panel}
                                            siteId={siteId}
                                            fullscreen
                                            onExpand={onExpand}
                                            expandable
                                            onOpenAddPanelDialog={onOpenAddPanelDialog}
                                        />
                                    </PanelFrameContext.Provider>
                                )}
                            </div>
                        );
                    return null;
                }}
                changeItemsForPage={(value: boolean) => {
                    isCollapsed.setValue(value);
                }}
            />
        </div>
    );
};

export default SecondaryPanels;
